var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import styles from './video-list.styles';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../button/button';
import { dictionary } from '@mf-cms/helpers';
import { MFElement } from '../mfelement/mfelement';
let PVideoList = class PVideoList extends MFElement {
    constructor() {
        super(...arguments);
        this.type = 'grid'; // "grid" | "list"
        this.sort = 'id';
        this.sortOrder = 'asc';
        this.showAllLink = '';
        this.videoBaseUrl = '';
        this.liveBaseUrl = '';
        this.culture = 'en-GB';
        this.showRuntime = false;
        this.hideDescription = false;
        this.videoItemLayout = 'default';
        this.manualVideos = [];
        this.videos = [];
        this.liveVideos = [];
        this.initialId = 0; //set inital folder id, needed for change in the admin
        this.initialSort = 'name';
        this.initialSortOrder = 'asc';
        this.initialLimit = 10;
        this.previousManualVideos = []; // Track previous manual videos
        this.showLeftSliderNav = false;
        this.showRightSliderNav = true;
        this.gridItemWidthFirstCheck = true;
        this.gridItemWidth = 200;
    }
    static { this.styles = styles; }
    /**
     * Connected callback lifecycle
     * @returns {void}
     */
    connectedCallback() {
        super.connectedCallback();
    }
    /**
     * First updated lifecycle
     * @returns {void}
     */
    async firstUpdated() {
        // Guard against SSR - only run in browser environment
        if (typeof window === 'undefined') {
            return;
        }
        this.accessToken = window.getAccessToken();
        if (this.manualVideos.length > 0) {
            this.videos = await this.getManualVideosAsync();
            this.requestUpdate();
            await this.updateComplete;
            this.initSlider();
            this.sideScrollTo(0, false);
            return;
        }
        if (this.folderId || this.keywords) {
            if (this.folderId) {
                this.initialId = parseInt(this.folderId);
            }
            this.initialLimit = parseInt(this.limit);
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            this.videos = await this.getVideosAsync();
            if (this.folderId || parseInt(this.folderId) == 0) {
                this.liveVideos = await this.getLiveVideos(this.folderId);
                // start live ticker for live videos, update every 10 seconds
                this.interval = setInterval(async () => {
                    this.liveVideos = await this.getLiveVideos(this.folderId);
                }, 10000);
            }
            this.requestUpdate();
            await this.updateComplete;
            this.initSlider();
            this.sideScrollTo(0, false);
        }
    }
    /**
     * Initialize slider
     * @returns {void}
     */
    initSlider() {
        // Guard against SSR - only run in browser environment
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }
        let gridItemDefaultWidth = getComputedStyle(document.documentElement).getPropertyValue(`--p-video-item-${this.videoItemLayout}-width`);
        if (gridItemDefaultWidth) {
            gridItemDefaultWidth = parseInt(gridItemDefaultWidth);
            this.gridItemWidth = gridItemDefaultWidth;
        }
        else {
            gridItemDefaultWidth = this.gridItemWidth;
        }
        if (this.resizeObserver)
            this.resizeObserver.disconnect();
        if (!this.ghostGridItemEl)
            return;
        this.resizeObserver = new ResizeObserver((entries) => this.onResize(entries, gridItemDefaultWidth));
        this.resizeObserver.observe(this.ghostGridItemEl);
    }
    /**
     * Handles resize events from the ResizeObserver
     * Updates grid item width and handles initial scroll positioning when the component first loads
     * @param {ResizeObserverEntry[]} entries - Array of resize observer entries containing size information
     * @param {number} gridItemDefaultWidth - The default width for grid items from CSS custom properties
     * @returns {void}
     */
    onResize(entries, gridItemDefaultWidth) {
        entries.forEach((entry) => {
            if (!entry.contentRect?.width)
                return;
            if (this.gridItemWidth == gridItemDefaultWidth &&
                this.gridItemWidthFirstCheck &&
                parseInt(entry.contentRect?.width) !== gridItemDefaultWidth &&
                this.videos &&
                this.videos.length !== 0) {
                this.gridItemWidthFirstCheck = false;
                this.sideScrollTo(0, false);
            }
            this.gridItemWidth = parseInt(entry.contentRect?.width);
            this.checkScroll();
        });
    }
    /**
     * Check if videoItemLayout changes and reinitialize slider
     * @param {Map} changedProperties Changed properties
     * @returns {void}
     */
    willUpdate(changedProperties) {
        if (!changedProperties.has('videoItemLayout'))
            return;
        this.initSlider();
    }
    async updated() {
        // Handle manual videos if they are set and have changed
        if (this.manualVideos.length > 0) {
            const hasManualVideosChanged = JSON.stringify(this.manualVideos) !== JSON.stringify(this.previousManualVideos);
            if (hasManualVideosChanged) {
                this.previousManualVideos = [...this.manualVideos];
                this.videos = await this.getManualVideosAsync();
                this.requestUpdate();
            }
            return;
        }
        // get videos if folderId is set to a new value
        if ((this.folderId && this.initialId !== parseInt(this.folderId)) ||
            (this.limit && this.initialLimit !== parseInt(this.limit))) {
            this.initialId = parseInt(this.folderId);
            this.initialLimit = parseInt(this.limit);
            this.videos = await this.getVideosAsync();
            this.liveVideos = await this.getLiveVideos(this.folderId);
            // start live ticker for live videos, update every 10 seconds
            this.interval = setInterval(async () => {
                this.liveVideos = await this.getLiveVideos(this.folderId);
            }, 10000);
        }
        // get videos if sort is set to a new value
        if ((this.sort && this.initialSort !== this.sort) ||
            (this.sortOrder && this.initialSortOrder !== this.sortOrder)) {
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            this.videos = await this.getVideosAsync();
            this.liveVideos = await this.getLiveVideos(this.folderId);
        }
        // clear videos if folderId and keywords are removed
        if (!this.folderId &&
            !this.keywords &&
            (this.videos.length > 0 || this.liveVideos.length > 0)) {
            this.videos = [];
            this.liveVideos = [];
        }
        this.checkScroll();
    }
    async getVideosAsync() {
        if (this.folderId || parseInt(this.folderId) == 0) {
            return await this.getVideosInFolderAsync();
        }
        if (this.keywords) {
            return await this.getVideosWithKeywordAsync();
        }
        return [];
    }
    /**
     * Fetches videos that match the specified keywords
     * @async
     * @returns {Promise<Array<Video>>} Videos
     * @throws {Error} When the API request fails
     */
    async getVideosWithKeywordAsync() {
        const keywordsArray = this.keywords.split(',');
        const perPage = this.excludeId ? this.limit + 1 : this.limit;
        let json;
        try {
            const response = await fetch(`${process.env.API_URL}/search/file?fields=id,name,description,poster,length&access_token=${this.accessToken}&page=1&per_page=${perPage}&sort=${this.sort}&order=${this.sortOrder}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    keywords: keywordsArray,
                    fileTypes: ['video']
                })
            });
            if (response.status === 404) {
                return [];
            }
            json = await response.json();
        }
        catch (e) {
            console.error(e);
            return [];
        }
        if (this.excludeId) {
            const videoFiles = json.filter((video) => video.id !== this.excludeId);
            return videoFiles.slice(0, this.limit);
        }
        return json;
    }
    /**
     * Fetches manual videos
     * @async
     * @returns {Promise<Array<Video>>} Videos
     * @throws {Error} When the API request fails
     */
    async getManualVideosAsync() {
        let json;
        try {
            const response = await fetch(`${process.env.API_URL}/search/file?fields=id,name,description,poster,length&access_token=${this.accessToken}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    fileIds: this.manualVideos.map((video) => video.id)
                })
            });
            json = await response.json();
        }
        catch (e) {
            console.error(e);
            return [];
        }
        // Replace name, title, description, poster with the values from the manual videos
        json = json.map((video) => {
            const manualVideo = this.manualVideos.find((v) => v.id === video.id);
            return {
                ...video,
                name: manualVideo?.title || video.name,
                description: manualVideo?.description || video.description,
                poster: manualVideo?.poster || video.poster
            };
        });
        // Order the videos by the order of the manual videos
        json = json.sort((a, b) => {
            const aIndex = this.manualVideos.findIndex((v) => v.id === a.id);
            const bIndex = this.manualVideos.findIndex((v) => v.id === b.id);
            return aIndex - bIndex;
        });
        return json;
    }
    /**
     * Fetches videos in the specified folder
     * @async
     * @returns {Promise<Array<Video>>} Videos
     * @throws {Error} When the API request fails
     */
    async getVideosInFolderAsync() {
        const folderId = parseInt(this.folderId);
        const perPage = this.excludeId ? this.limit + 1 : this.limit;
        let json;
        try {
            const url = folderId == 0
                ? `${process.env.API_URL}/files/latest?fields=id,name,description,poster,length&type=video&access_token=${this.accessToken}&page=1&per_page=${perPage}&sort=${this.sort}&order=${this.sortOrder}`
                : `${process.env.API_URL}/folder/${folderId}/files?fields=id,name,description,poster,length&type=video&access_token=${this.accessToken}&page=1&per_page=${perPage}&sort=${this.sort}&order=${this.sortOrder}`;
            const response = await fetch(url);
            if (response.status === 404) {
                return [];
            }
            json = await response.json();
        }
        catch (e) {
            console.error(e);
            return [];
        }
        if (this.excludeId) {
            const videoFiles = json.filter((video) => video.id !== this.excludeId);
            return videoFiles.slice(0, this.limit);
        }
        return json;
    }
    async getLiveVideos(folderId) {
        if (!folderId)
            return [];
        let response;
        const url = `${process.env.API_URL}/folder/${folderId}/livestreams?fields=any&access_token=${this.accessToken}&sort=liveAt&order=desc`;
        try {
            response = await fetch(url);
        }
        catch (e) {
            console.error(e);
            return [];
        }
        if (response.status !== 200) {
            console.error(`Could not fetch livestream, error: ${response.status}`);
            return [];
        }
        const json = await response.json();
        return json.sort((a, b) => {
            if (a.status === 'BROADCASTING' && b.status !== 'BROADCASTING')
                return -1;
            if (a.status !== 'BROADCASTING' && b.status === 'BROADCASTING')
                return 1;
            return new Date(a.liveAt).getTime() - new Date(b.liveAt).getTime();
        });
    }
    /**
     * Disconnected callback lifecycle
     * @returns {void}
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        clearInterval(this.interval);
        this.resizeObserver.unobserve(this.ghostGridItemEl);
    }
    /**
     * Slide Left
     * @return {void}
     */
    slideLeft() {
        this.slideByPages(-1);
    }
    /**
     * Slide Right
     * @return {void}
     */
    slideRight() {
        this.slideByPages(1);
    }
    /**
     * @param {(-1|1)} direction -1 left, 1 right
     * @return {void}
     */
    slideByPages(direction) {
        const scrollDistance = this.getScrollDistance();
        const step = this.gridItemWidth + this.getItemGap();
        const target = Math.round((this.wrapperEl.scrollLeft + direction * scrollDistance) / step) * step;
        this.sideScrollTo(direction < 0 ? Math.max(0, target) : target);
    }
    getItemGap() {
        return parseFloat(getComputedStyle(this).getPropertyValue('--p-video-grid-gap')) || 0;
    }
    getScrollDistance() {
        const gap = this.getItemGap();
        const step = this.gridItemWidth + gap;
        const itemsPerPage = Math.max(1, Math.floor(this.containerOuterEl.offsetWidth / step));
        return itemsPerPage * step;
    }
    /**
     * Check Scroll position to make sure the right nav buttons are visible
     * @return {void}
     */
    checkScroll() {
        if (!this.wrapperEl)
            return;
        this.showLeftSliderNav = this.wrapperEl.scrollLeft >= 5;
        this.showRightSliderNav =
            this.wrapperEl.scrollWidth - this.wrapperEl.offsetWidth - this.wrapperEl.scrollLeft >= 5;
    }
    /**
     * Handle keydown
     * @param {number} targetPosition - where to horizontally scroll
     * @param {boolean} smoothScroll - how to horizontally scroll
     * @return {void}
     */
    sideScrollTo(targetPosition = 0, smoothScroll = true) {
        this.wrapperEl.scrollTo({
            top: 0,
            left: targetPosition,
            behavior: smoothScroll ? 'smooth' : 'instant'
        });
        setTimeout(() => {
            this.checkScroll();
        }, smoothScroll ? 600 : 10); // chrome requires this long for smooth scroll animation
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        if (this.videos.length == 0 && this.liveVideos.length == 0 && this.manualVideos.length == 0) {
            return html ``;
        }
        const classes = {
            [`${this.type}`]: true,
            [`layout-${this.videoItemLayout}`]: true
        };
        const chevron = html ` <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="17" height="17" fill="currentColor" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708" /> </svg> `;
        const ghostGrid = this.type == 'slider'
            ? html ` <div class="ghost-grid" aria-hidden="true"> <div class="ghost-grid-item" data-value=${this.gridItemWidth}></div> </div> `
            : '';
        return html ` <section style=${'--ghost-item-width: ' + this.gridItemWidth + 'px'} class="video-list${classMap(classes)}" aria-label=${this.heading ? `${dictionary(this.culture, 'videoList', 'ariaVideoList')} ` + this.heading
            : `${dictionary(this.culture, 'videoList', 'ariaVideoListEmpty')}`}
      >
        <header>
          <slot name="header"></slot>
          ${this.showAllLink
            ? html ` <p-button size="medium" color="arrow" href=${this.showAllLink} >${dictionary(this.culture, 'videoList', 'showAll')}</p-button > `
            : ''}
        </header>
        <div class="container-outer">
          ${this.type == 'slider'
            ? html ` <button class="slider-nav-left" aria-controls=${this.sliderId + '-list'} ?disabled=${!this.showLeftSliderNav} aria-label=${dictionary(this.culture, 'videoList', 'slideLeft')} @click=${this.slideLeft} > ${chevron} </button> <button class="slider-nav-right" aria-controls=${this.sliderId + '-list'} ?disabled=${!this.showRightSliderNav} aria-label=${dictionary(this.culture, 'videoList', 'slideRight')} @click=${this.slideRight} > ${chevron} </button> `
            : ''}

          <div class="container">
            <div
              class="wrapper"
              role="list"
              id=${ifDefined(this.sliderId ? this.sliderId + '-list' : undefined)}
              aria-label=${ifDefined(this.heading || undefined)}
              @wheel=${this.checkScroll}
            >
              ${this.renderVideos()}
            </div>
          </div>
        </div>
        ${ghostGrid}
      </section>
    `;
    }
    /**
     * Renders the video section of the component.
     *
     * If in preview mode and there are no videos (live or recorded),
     * it renders a static set of placeholder `<p-video-item>` elements.
     * Otherwise, it renders live and dynamic videos.
     *
     * @returns {TemplateResult} The rendered video template.
     */
    renderVideos() {
        if (this.preview && this.videos.length == 0 && this.liveVideos.length == 0) {
            return html ` <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> `;
        }
        let remaining = parseInt(this.limit);
        const liveVideosHTML = this.renderLiveVideos(remaining);
        remaining -= this.liveVideos.length;
        const dynamicVideosHTML = this.renderDynamicVideos(remaining);
        return html ` ${liveVideosHTML} ${dynamicVideosHTML} `;
    }
    /**
     * Renders the list of recorded (on-demand) videos.
     *
     * Iterates over `this.videos` and creates a `<p-video-item>` element
     * for each video, including metadata such as title, description,
     * poster, and duration.
     *
     * @param {number} remaining - remaning videos
     * @returns {TemplateResult} The rendered list of recorded video items,
     * or an empty template if no videos are available.
     */
    renderDynamicVideos(remaining) {
        if (!this.videos)
            return html ``;
        return html `${this.videos.map((video) => {
            remaining--;
            if (remaining < 0) {
                return html ``;
            }
            return html ` <p-video-item culture=${this.culture} heading-tag=${this.heading ? 'h3' : 'h2'} role="listitem" link=${this.videoBaseUrl + '/' + video.id} video-id=${video.id} summary=${video.description} heading=${video.name} poster=${video.poster} duration=${this.showRuntime ? video.length : ''} ?hide-description=${this.hideDescription} layout=${this.videoItemLayout} > </p-video-item> `;
        })}`;
    }
    /**
     * Renders the list of live or scheduled video streams.
     *
     * Iterates over `this.liveVideos` and creates a `<p-video-item>` for each one,
     * setting attributes based on its broadcast status (live, upcoming, or finished).
     *
     * @param {number} remaining - number of remaining videos to render
     * @returns {TemplateResult} The rendered list of live video items,
     * or an empty template if no live videos are available.
     */
    renderLiveVideos(remaining) {
        if (!this.liveVideos)
            return html ``;
        return html `${this.liveVideos.map((video) => {
            remaining--;
            if (remaining < 0) {
                return html ``;
            }
            return html ` <p-video-item culture=${this.culture} heading-tag=${this.heading ? 'h3' : 'h2'} role="listitem" ?is-live=${video.status == 'BROADCASTING'} ?is-upcoming=${video.status == 'PENDING'} ?ended=${video.status == 'FINISHED'} link=${this.liveBaseUrl + '/' + video.id} video-id=${video.id} summary=${video.description} heading=${video.title} poster=${video.poster} duration=${video.length} date=${video.liveAt} ?hide-description=${this.hideDescription} layout=${this.videoItemLayout} > </p-video-item> `;
        })}`;
    }
};
__decorate([
    property({ type: String, reflect: true })
], PVideoList.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true })
], PVideoList.prototype, "heading", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'heading-tag' })
], PVideoList.prototype, "headingTag", void 0);
__decorate([
    property({ type: String, reflect: true })
], PVideoList.prototype, "sort", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'sort-order' })
], PVideoList.prototype, "sortOrder", void 0);
__decorate([
    property({ type: Number, reflect: true })
], PVideoList.prototype, "limit", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PVideoList.prototype, "preview", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PVideoList.prototype, "dark", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'show-all-link' })
], PVideoList.prototype, "showAllLink", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'video-base-url' })
], PVideoList.prototype, "videoBaseUrl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'live-base-url' })
], PVideoList.prototype, "liveBaseUrl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'slider-id' })
], PVideoList.prototype, "sliderId", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'folder-id' })
], PVideoList.prototype, "folderId", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'keywords' })
], PVideoList.prototype, "keywords", void 0);
__decorate([
    property({ type: String })
], PVideoList.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-runtime' })
], PVideoList.prototype, "showRuntime", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'exclude-id' })
], PVideoList.prototype, "excludeId", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'hide-description' })
], PVideoList.prototype, "hideDescription", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'video-item-layout' })
], PVideoList.prototype, "videoItemLayout", void 0);
__decorate([
    property({ type: Array, attribute: 'manual-videos' })
], PVideoList.prototype, "manualVideos", void 0);
__decorate([
    property()
], PVideoList.prototype, "videos", void 0);
__decorate([
    property()
], PVideoList.prototype, "liveVideos", void 0);
__decorate([
    state()
], PVideoList.prototype, "showLeftSliderNav", void 0);
__decorate([
    state()
], PVideoList.prototype, "showRightSliderNav", void 0);
__decorate([
    state()
], PVideoList.prototype, "gridItemWidthFirstCheck", void 0);
__decorate([
    state()
], PVideoList.prototype, "gridItemWidth", void 0);
__decorate([
    query('.wrapper')
], PVideoList.prototype, "wrapperEl", void 0);
__decorate([
    query('.container')
], PVideoList.prototype, "containerEl", void 0);
__decorate([
    query('.container-outer')
], PVideoList.prototype, "containerOuterEl", void 0);
__decorate([
    query('.ghost-grid-item')
], PVideoList.prototype, "ghostGridItemEl", void 0);
PVideoList = __decorate([
    customElement('p-video-list')
    /**
     * PVideoList component
     *
     * @class PVideoList
     * @typedef {PVideoList}
     * @extends {LitElement}
     */
], PVideoList);
export { PVideoList };
